import { useState, useEffect, useCallback, useRef } from "react";
import { todayISO } from "@/lib/utils";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import Badge from "@/components/ui/badge/Badge";
import { Modal } from "@/components/ui/modal";
import ConfirmModal from "@/components/ui/modal/ConfirmModal";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import {
  expenseService,
  expenseCategoryService,
} from "@/services/expenseService";
import { attachmentService } from "@/services/attachmentService";
import { handleApiError } from "@/services/api";
import AttachmentUpload from "@/components/form/AttachmentUpload";
import AttachmentViewer from "@/components/common/AttachmentViewer";
import DateRangeFilter, {
  getDefaultDateRange,
} from "@/components/common/DateRangeFilter";
import type {
  Attachment,
  Expense,
  ExpenseCategory,
  ExpenseMethod,
} from "@/types/models";
import { useIsViewer } from "@/hooks/useRole";
import { useSortable } from "@/hooks/useSortable";
import SortableHeader from "@/components/ui/table/SortableHeader";
import { useAuthStore } from "@/store/useAuthStore";
import Pagination from "@/components/ui/pagination/Pagination";
import PrintLink from "@/components/print/PrintLink";
import { printPaymentVoucher } from "@/utils/printPaymentVoucher";

const selectClass =
  "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

const METHOD_LABELS: Record<string, string> = {
  cash: "Cash",
  bank_transfer: "Bank Transfer",
  cheque: "Cheque",
  upi: "UPI",
};

const methodLabel = (m?: string) => (m ? METHOD_LABELS[m] || m : "-");

export default function ExpensesPage() {
  const isViewer = useIsViewer();
  const { sortConfig, requestSort } = useSortable("date", "desc");

  // Table state
  const [entries, setEntries] = useState<Expense[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Categories
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);

  // Filters
  const [filterFrom, setFilterFrom] = useState(() => getDefaultDateRange().from);
  const [filterTo, setFilterTo] = useState(() => getDefaultDateRange().to);
  const [filterCategoryId, setFilterCategoryId] = useState("");

  // Create/edit expense modal
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  // Form state
  const [date, setDate] = useState(todayISO());
  const [categoryId, setCategoryId] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [partyName, setPartyName] = useState("");
  const [method, setMethod] = useState<ExpenseMethod>("cash");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [notes, setNotes] = useState("");

  // Attachment state — receipts/invoices are always optional
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [pendingAttachmentIds, setPendingAttachmentIds] = useState<number[]>([]);
  // Bumped on every modal open/close so an in-flight attachment fetch from a
  // previous open can't land in the current form.
  const modalGeneration = useRef(0);
  const attachmentsTouched = useRef(false);

  const loadCategories = async () => {
    try {
      const data = await expenseCategoryService.list();
      setCategories((data as ExpenseCategory[]) ?? []);
    } catch {
      // silently fail, categories will just be empty
    }
  };

  const loadExpenses = useCallback(async (fromDate?: string, toDate?: string, newOffset?: number) => {
    setError("");
    try {
      const o = newOffset !== undefined ? newOffset : offset;
      const params: Record<string, string | number> = { limit, offset: o };
      const f = fromDate !== undefined ? fromDate : filterFrom;
      const t = toDate !== undefined ? toDate : filterTo;
      if (f) params.from = f;
      if (t) params.to = t;
      if (filterCategoryId) params.category_id = filterCategoryId;
      params.sort_by = sortConfig.key;
      params.sort_order = sortConfig.direction;
      const res = await expenseService.list(params);
      if (Array.isArray(res)) {
        setEntries(res as unknown as Expense[]);
        setTotal((res as unknown as Expense[]).length);
      } else {
        const paginated = res as unknown as {
          data: Expense[];
          meta: { total: number };
        };
        setEntries(paginated.data ?? []);
        setTotal(paginated.meta?.total ?? 0);
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [offset, filterFrom, filterTo, filterCategoryId, sortConfig]);

  useEffect(() => {
    loadCategories();
    loadExpenses();
  }, [loadExpenses]);

  const openCreate = () => {
    modalGeneration.current += 1;
    setEditingId(null);
    setDate(todayISO());
    setCategoryId(categories.length > 0 ? String(categories[0].id) : "");
    setAmount("");
    setDescription("");
    setPartyName("");
    setMethod("cash");
    setInvoiceNumber("");
    setNotes("");
    setAttachments([]);
    setPendingAttachmentIds([]);
    setError("");
    setModalOpen(true);
  };

  const openEdit = (exp: Expense) => {
    const generation = (modalGeneration.current += 1);
    setEditingId(exp.id);
    setDate(exp.date.split("T")[0]);
    setCategoryId(String(exp.category_id));
    setAmount(String(exp.amount));
    setDescription(exp.description || "");
    setPartyName(exp.party_name || "");
    setMethod(exp.method || "cash");
    setInvoiceNumber(exp.invoice_number || "");
    setNotes(exp.notes || "");
    setAttachments([]);
    setPendingAttachmentIds([]);
    setError("");
    setModalOpen(true);
    // Load existing attachments for this expense. The generation check drops a
    // late response after the user has moved on — otherwise this expense's
    // receipts render inside the *next* modal, where AttachmentUpload's remove
    // button would delete them server-side from a form for a different expense.
    attachmentService
      .listBySource("expense", exp.id)
      .then((data) => {
        if (modalGeneration.current === generation) setAttachments(data ?? []);
      })
      .catch(() => {
        if (modalGeneration.current === generation) setAttachments([]);
      });
  };

  // Closing without saving: uploads/removals inside the modal already hit the
  // server, so the row's receipt count is stale unless we refetch.
  const closeModal = () => {
    modalGeneration.current += 1;
    setModalOpen(false);
    if (attachmentsTouched.current) {
      attachmentsTouched.current = false;
      loadExpenses();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      const payload = {
        date,
        category_id: parseInt(categoryId),
        amount: parseFloat(amount),
        method,
        invoice_number: invoiceNumber.trim() || undefined,
        description: description || undefined,
        party_name: partyName || undefined,
        notes: notes || undefined,
        attachment_ids:
          pendingAttachmentIds.length > 0 ? pendingAttachmentIds : undefined,
      };
      if (editingId) {
        await expenseService.update(editingId, payload);
      } else {
        await expenseService.create(payload);
      }
      attachmentsTouched.current = false;
      modalGeneration.current += 1;
      setModalOpen(false);
      setLoading(true);
      await loadExpenses();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirmId) return;
    try {
      await expenseService.delete(deleteConfirmId);
      setDeleteConfirmId(null);
      setLoading(true);
      await loadExpenses();
    } catch (err) {
      setError(handleApiError(err));
      setDeleteConfirmId(null);
    }
  };

  const handleDateRangeApply = (from: string, to: string) => {
    setFilterFrom(from);
    setFilterTo(to);
    setOffset(0);
    setLoading(true);
    loadExpenses(from, to, 0);
  };

  const handlePrev = () => {
    const newOffset = Math.max(0, offset - limit);
    setOffset(newOffset);
    loadExpenses(undefined, undefined, newOffset);
  };

  const handleNext = () => {
    const newOffset = offset + limit;
    setOffset(newOffset);
    loadExpenses(undefined, undefined, newOffset);
  };

  const farm = useAuthStore((s) => s.farm);
  const [printingId, setPrintingId] = useState<number | null>(null);

  const formatCurrency = (n: number) =>
    n.toLocaleString("en-IN", { maximumFractionDigits: 2 });

  const categoryNameMap = new Map(categories.map((c) => [c.id, c.name]));

  const handlePrint = (exp: Expense) => {
    setPrintingId(exp.id);
    const catName = exp.category_name || categoryNameMap.get(exp.category_id) || "";
    const onAccountOf = [catName, exp.description].filter(Boolean).join(" — ");
    printPaymentVoucher({
      farmName: farm?.name || "Farm",
      voucherNumber: `EXP-${String(exp.id).padStart(5, "0")}`,
      date: new Date(exp.date).toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" }),
      amount: exp.amount,
      paidTo: exp.party_name || "",
      onAccountOf,
    });
    setPrintingId(null);
  };

  // Summary: total amount for current view
  const totalExpenses = entries.reduce((sum, e) => sum + e.amount, 0);

  return (
    <>
      <PageMeta
        title="Expenses | Poultry Book"
        description="Track farm expenses"
      />
      <PageBreadcrumb pageTitle="Expenses" />

      <div className="card">
        <div className="flex items-center justify-between px-5 py-4 sm:px-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            Expenses{" "}
            {total > 0 && (
              <span className="text-sm font-normal text-gray-500">
                ({total}) &mdash; Total: &#8377;{formatCurrency(totalExpenses)}
              </span>
            )}
          </h3>
          {!isViewer && (
            <Button size="sm" onClick={openCreate}>
              Add Expense
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 px-5 pb-4 sm:px-6">
          <DateRangeFilter onApply={handleDateRangeApply} />
          <select
            value={filterCategoryId}
            onChange={(e) => {
              setFilterCategoryId(e.target.value);
              setOffset(0);
            }}
            className={`${selectClass} max-w-[200px]`}
          >
            <option value="">All categories</option>
            {categories
              .filter((c) => c.active)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
        </div>

        {error && !modalOpen && (
          <div className="mx-5 mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500 sm:mx-6">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          {loading ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              Loading...
            </div>
          ) : entries.length === 0 && !error ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              No expenses in the selected period.
            </div>
          ) : entries.length === 0 ? null : (
            <Table>
              <TableHeader>
                <TableRow className="border-t border-gray-200 dark:border-gray-800">
                  <SortableHeader label="Date" sortKey="date" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader label="Category" sortKey="category" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader label="Amount" sortKey="amount" sortConfig={sortConfig} onSort={requestSort} />
                  <TableCell
                    isHeader
                    className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400"
                  >
                    Method
                  </TableCell>
                  <SortableHeader label="Paid To" sortKey="party_name" sortConfig={sortConfig} onSort={requestSort} />
                  <TableCell
                    isHeader
                    className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400"
                  >
                    Invoice #
                  </TableCell>
                  <TableCell
                    isHeader
                    className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400"
                  >
                    Description
                  </TableCell>
                  <SortableHeader label="Notes" sortKey="notes" sortConfig={sortConfig} onSort={requestSort} />
                  <TableCell
                    isHeader
                    className="px-3 py-3 text-center text-sm font-medium text-gray-500 dark:text-gray-400"
                  >
                    Receipt
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">{""}</TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((exp) => (
                  <TableRow
                    key={exp.id}
                    className="border-t border-gray-100 dark:border-gray-800"
                  >
                    <TableCell className="px-5 py-3 text-sm text-gray-800 dark:text-white/90">
                      {new Date(exp.date).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="px-5 py-3">
                      <Badge size="sm" color="info">
                        {exp.category_name ||
                          categoryNameMap.get(exp.category_id) ||
                          `#${exp.category_id}`}
                      </Badge>
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-right font-medium text-gray-800 dark:text-white/90">
                      &#8377;{formatCurrency(exp.amount)}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {methodLabel(exp.method)}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {exp.party_name || "-"}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {exp.invoice_number || "-"}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {exp.description || "-"}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {exp.notes || "-"}
                    </TableCell>
                    <TableCell className="px-3 py-3 text-center">
                      <AttachmentViewer
                        sourceType="expense"
                        sourceId={exp.id}
                        count={exp.attachment_count ?? 0}
                      />
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm">
                      <div className="flex items-center gap-2">
                        <PrintLink onClick={() => handlePrint(exp)} loading={printingId === exp.id} />
                        {!isViewer && (
                          <>
                            <button onClick={() => openEdit(exp)} className="text-brand-500 hover:text-brand-600 dark:text-brand-400 text-xs font-medium">Edit</button>
                            <button onClick={() => setDeleteConfirmId(exp.id)} className="text-error-500 hover:text-error-600 dark:text-error-400 text-xs font-medium">Delete</button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <Pagination total={total} limit={limit} offset={offset} onPrev={handlePrev} onNext={handleNext} />
      </div>

      {/* Create Expense Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        className="max-w-md p-5 sm:p-8 mx-4"
      >
        <h4 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90">
          {editingId ? "Edit Expense" : "Add Expense"}
        </h4>

        {error && modalOpen && (
          <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="expDate">Date *</Label>
            <Input
              id="expDate"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>

          <div>
            <Label htmlFor="expCategory">Category *</Label>
            <select
              id="expCategory"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className={selectClass}
              required
            >
              <option value="" disabled>
                Select category
              </option>
              {categories
                .filter((c) => c.active)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
          </div>

          <div>
            <Label htmlFor="expParty">Paid To</Label>
            <Input
              id="expParty"
              value={partyName}
              onChange={(e) => setPartyName(e.target.value)}
              placeholder="e.g. Ramesh, HP Petrol Pump"
              maxLength={200}
            />
          </div>

          <div>
            <Label htmlFor="expAmount">Amount (&#8377;) *</Label>
            <Input
              id="expAmount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 5000"
              required
              min={0.01}
              step="0.01"
            />
          </div>

          <div>
            <Label htmlFor="expMethod">Payment Method *</Label>
            <select
              id="expMethod"
              value={method}
              onChange={(e) => setMethod(e.target.value as ExpenseMethod)}
              className={selectClass}
              required
            >
              <option value="cash">Cash</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="cheque">Cheque</option>
              <option value="upi">UPI</option>
            </select>
          </div>

          <div>
            <Label htmlFor="expInvoice">Invoice Number</Label>
            <Input
              id="expInvoice"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder="e.g. INV-2026-1234"
              maxLength={100}
            />
          </div>

          <div>
            <Label htmlFor="expDesc">Description</Label>
            <Input
              id="expDesc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Monthly salary for 3 workers"
              maxLength={255}
            />
          </div>

          <div>
            <Label htmlFor="expNotes">Notes</Label>
            <textarea
              id="expNotes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
              rows={2}
              className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            />
          </div>

          {/* Receipts / Invoices — optional */}
          <AttachmentUpload
            attachments={attachments}
            onUpload={(a) => {
              attachmentsTouched.current = true;
              setAttachments((prev) => [...prev, a]);
              setPendingAttachmentIds((prev) => [...prev, a.id]);
            }}
            onDelete={(id) => {
              attachmentsTouched.current = true;
              setAttachments((prev) => prev.filter((a) => a.id !== id));
              setPendingAttachmentIds((prev) => prev.filter((aid) => aid !== id));
            }}
            disabled={saving}
          />

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={closeModal}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={saving || !categoryId || !amount}
            >
              {saving ? "Saving..." : editingId ? "Update Expense" : "Add Expense"}
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        isOpen={deleteConfirmId !== null}
        title="Delete Expense"
        message="Are you sure you want to delete this expense? This action cannot be undone."
        onConfirm={handleDelete}
        onClose={() => setDeleteConfirmId(null)}
      />
    </>
  );
}
